import React from 'react';
import PropTypes from 'prop-types';
import {List} from 'immutable';
import {upperFirst} from 'lib/stringUtils';
import appStore from 'client/AppStore';
import sessionStore from 'client/Session/SessionStore';
import SearchDropdown from 'sg/Dropdowns/SearchDropdown/SearchDropdown.react';
import {stripNonAlphaNumeric} from 'lib/stringUtils';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import {SEARCH_PARAMETERS, getURLSearchParameter} from '../URLSearchParameters';

import {resource} from '@albert-io/json-api-framework/request/builder';

const getBaseSubjectQuery = () =>
  resource('authoring_subjects_v1')
    .mandarkEndpoint(
      sessionStore.isSuper()
        ? ['authoring_subjects_v1']
        : ['authors_v1', sessionStore.getUserId(), 'authoring_subjects_v1']
    )
    .pageSize('infinite')
    .sort('domain,name');

class SubjectSearchDropdown extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    handleOptionChange: PropTypes.func,
    showQuestionCount: PropTypes.bool,
    storeName: PropTypes.string,
    subjects: PropTypes.instanceOf(List),
    queryParamName: PropTypes.string
  };

  state = {
    shouldLoadSubjectsQuery: false
  };

  getSubjectsWithMetaQuery() {
    const userId = sessionStore.getUserId();
    return (
      getBaseSubjectQuery()
        .withMeta('authoring_subject_v1')
        /**
         * Since `owner_` is not _actually_ a defined resource (maybe it will be),
         * we have to use `.customQuery` instead of `.metaContext`
         */
        .customQuery(
          {
            meta: {
              context: {
                owner: {
                  id: userId
                }
              }
            }
          },
          sessionStore.isSuper() === false
        )
        .customQuery({
          meta: {
            context: {
              subject: {
                counts: true
              }
            }
          }
        })
    );
  }

  showLoadSubjectsQuery = () => {
    this.setState({
      shouldLoadSubjectsQuery: true
    });
  };

  getDefaultOption = () => {
    const subjectId = appStore.routerProps.location.query[this.props.queryParamName];
    const subjectQuery =
      subjectId &&
      resource('authoring_subject_v1').mandarkEndpoint(['authoring_subjects_v1', subjectId]);

    if (!subjectId || !subjectQuery.isResourcePopulated()) {
      return '';
    }
    return subjectQuery.getResource();
  };

  getOptions = () => {
    return this.state.shouldLoadSubjectsQuery
      ? this.getSubjectsWithMetaQuery().getResource()
      : List();
  };

  filterOptions(option, searchString) {
    return stripNonAlphaNumeric(
      `${option.get('domain')} ${option.get('name')}`.toLowerCase()
    ).includes(stripNonAlphaNumeric(searchString.toLowerCase()));
  }

  renderOptions = (option) => {
    let displayCount = null;
    if (this.props.showQuestionCount && this.getSubjectsWithMetaQuery().isResourcePopulated()) {
      const subjectsWithCounts = this.getSubjectsWithMetaQuery()
        .getResource()
        .asMap();
      const count = subjectsWithCounts.has(option.getId())
        ? this.getFilteredCountOfQuestions(subjectsWithCounts.get(option.getId()))
        : 0;
      displayCount = `(${count})`;
    } else if (this.props.showQuestionCount) {
      displayCount = <span className='fa fa-spinner fa-pulse fa-fw' />;
    }

    return (
      <div>
        <small>{option.get('domain')}</small>
        <br />
        {option.get('name')} {displayCount}
      </div>
    );
  };

  getFilteredCountOfQuestions(option) {
    const status = getURLSearchParameter(SEARCH_PARAMETERS.status);
    const method = status ? `getCountOf${upperFirst(status)}Questions` : `getCountOfQuestions`;
    return option.getMeta()[method]();
  }

  render() {
    return (
      <SearchDropdown
        openOnFocus
        resetOnUnmount
        className={this.props.className}
        customClearOption=''
        defaultOption={this.getDefaultOption()}
        displayName='name'
        filterFunc={this.filterOptions}
        handleOptionChange={this.props.handleOptionChange}
        label='Subject'
        onFocus={this.showLoadSubjectsQuery}
        options={this.props.subjects}
        placeholder='All Subjects'
        renderFunc={this.renderOptions}
        storeName={this.props.storeName}
      />
    );
  }
}

export default awaitMandarkQueries(
  () => ({
    queries: {
      subjects: getBaseSubjectQuery()
    }
  }),
  SubjectSearchDropdown
);
